// BUILD-042 cycle 12 (FE-3b) - the pure rules behind the guided-setup Documents step: which forms can be chosen, what is preselected, how each assignment is
// described, and how a batch of assignments is run. No DOM, no React, no network (the writer is injected). Tested under plain Node (documentsModel.test.mjs).
//
// Nothing here assigns anything on its own. A "suggested" form is only a preselected checkbox; an assignment exists only after HR presses Assign selected and the
// server has accepted that one request. A result row says what the SERVER answered, never what the browser hoped for.
import type { HrAssignmentPrompt, HrDocumentAssignment, HrDocumentTemplate } from '../../../api/client';

type Status = HrDocumentAssignment['status'];
type AssignmentLike = Pick<HrDocumentAssignment, 'assignmentId' | 'templateId' | 'templateVersionId' | 'status' | 'cancelledAt' | 'movedToHistoryAt'> & Partial<Pick<HrDocumentAssignment, 'equipmentCheckout'>>;
type TemplateLike = Pick<HrDocumentTemplate, 'templateId' | 'name' | 'retiredAt' | 'recordKind' | 'versions'> & Partial<Pick<HrDocumentTemplate, 'description' | 'suggestedForOnboarding'>>;

/** The only words used for an assignment's standing. They are the REAL statuses; "signed" is never inferred from an assignment. */
export const STATUS_CHIP: Record<Exclude<Status, 'cancelled'>, { label: string; tone: 'amber' | 'orange' | 'red' | 'blue' }> = {
  awaiting_return: { label: 'Waiting on employee', tone: 'amber' },
  awaiting_hr_review: { label: 'Needs HR review', tone: 'orange' },
  needs_correction: { label: 'Needs correction', tone: 'red' },
  reviewed_complete: { label: 'Reviewed', tone: 'blue' },
};

/** Cancelled records are not part of this list (the step counts what is live); everything else has a chip. */
export function statusChip(status: Status): { label: string; tone: 'amber' | 'orange' | 'red' | 'blue' } | null {
  return status === 'cancelled' ? null : STATUS_CHIP[status] ?? null;
}

/** Live = HR's assignment still stands: not cancelled and not moved to History. Same definition the server uses for the step summary. */
export function isLive(a: AssignmentLike): boolean {
  return a.status !== 'cancelled' && !a.cancelledAt && !a.movedToHistoryAt;
}

/** Template ids that belong to the Equipment step (an equipment checkout rides on a document assignment but is never listed or assigned here). */
export function equipmentTemplateIds(templates: TemplateLike[]): Set<string> {
  return new Set(templates.filter((t) => t.recordKind === 'equipment').map((t) => t.templateId));
}

/** The assignments the step lists: live, and not an equipment checkout. Newest first is the server's order; it is preserved. */
export function listedAssignments<A extends AssignmentLike>(assignments: A[], equipmentIds: Set<string>): A[] {
  return assignments.filter((a) => isLive(a) && !a.equipmentCheckout && !equipmentIds.has(a.templateId));
}

export interface FormRow {
  templateId: string;
  versionId: string;
  versionNumber: number;
  name: string;
  description: string | null;
  suggested: boolean;
  /** A live assignment of THIS (latest) version already exists for this employee: shown as "Already assigned", never selectable. */
  assigned: boolean;
  /** A live assignment of an EARLIER version exists. Informational only (a new version may legitimately be assigned). */
  earlierVersionAssigned: boolean;
}

/** One row per active, assignable template at its latest version. Retired and equipment forms never appear. Open suggestions first, then by name. */
export function formRows(templates: TemplateLike[], assignments: AssignmentLike[]): FormRow[] {
  const live = assignments.filter(isLive);
  return templates
    .filter((t) => !t.retiredAt && t.recordKind !== 'equipment' && t.versions.length > 0)
    .map((t): FormRow => {
      const latest = t.versions[0];
      const mine = live.filter((a) => a.templateId === t.templateId);
      return {
        templateId: t.templateId, versionId: latest.versionId, versionNumber: latest.versionNumber,
        name: t.name, description: t.description ?? null,
        suggested: t.suggestedForOnboarding === true,
        assigned: mine.some((a) => a.templateVersionId === latest.versionId),
        earlierVersionAssigned: mine.length > 0 && !mine.some((a) => a.templateVersionId === latest.versionId),
      };
    })
    // HR's suggestions that can still be assigned come first (so they are all visible without scrolling), then everything else by name.
    .sort((a, b) => Number(b.suggested && !b.assigned) - Number(a.suggested && !a.assigned) || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || a.templateId.localeCompare(b.templateId));
}

/** Case-insensitive match on the form name (and description). An empty query matches everything. */
export function filterRows(rows: FormRow[], query: string): FormRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => r.name.toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q));
}

/** Only HR-configured suggestions that can actually be assigned right now are preselected. Never anything else, never a hardcoded name or count. */
export function suggestedIds(rows: FormRow[]): string[] {
  return rows.filter((r) => r.suggested && !r.assigned).map((r) => r.templateId);
}

export function selectableIds(rows: FormRow[]): string[] {
  return rows.filter((r) => !r.assigned).map((r) => r.templateId);
}

/** Selection is kept across search and scroll; this only ever adds/removes the ids that are currently visible and selectable. */
export function setVisibleSelected(selected: ReadonlySet<string>, visible: FormRow[], on: boolean): Set<string> {
  const next = new Set(selected);
  for (const id of selectableIds(visible)) { if (on) next.add(id); else next.delete(id); }
  return next;
}
export function toggleOne(selected: ReadonlySet<string>, row: FormRow): Set<string> {
  const next = new Set(selected);
  if (row.assigned) return next;
  if (next.has(row.templateId)) next.delete(row.templateId); else next.add(row.templateId);
  return next;
}
/** Drop selected ids that are no longer selectable (assigned since, retired, removed). The selection never points at a form that cannot be assigned. */
export function pruneSelection(selected: ReadonlySet<string>, rows: FormRow[]): Set<string> {
  const ok = new Set(selectableIds(rows));
  return new Set([...selected].filter((id) => ok.has(id)));
}
/** State of the select-all-visible checkbox: 'all' | 'some' | 'none' over the visible selectable rows. */
export function visibleSelectionState(selected: ReadonlySet<string>, visible: FormRow[]): 'all' | 'some' | 'none' {
  const ids = selectableIds(visible);
  if (ids.length === 0) return 'none';
  const n = ids.filter((id) => selected.has(id)).length;
  return n === 0 ? 'none' : n === ids.length ? 'all' : 'some';
}

// ─── Values HR types at assignment (the existing "prompts" flow) ─────────────────────────────────────────────────────────────────
/** Selected forms that ask HR for values, with what is still missing for each. Assigning is blocked until every required value is entered. */
export function valueNeeds(
  selected: ReadonlySet<string>, rows: FormRow[], promptsByVersion: Record<string, HrAssignmentPrompt[]>,
  values: Record<string, Record<string, string>>, missing: (prompts: HrAssignmentPrompt[], v: Record<string, string>) => string[],
): Array<{ row: FormRow; prompts: HrAssignmentPrompt[]; missing: string[] }> {
  return rows
    .filter((r) => selected.has(r.templateId) && !r.assigned)
    .map((r) => ({ row: r, prompts: promptsByVersion[r.versionId] ?? [] }))
    .filter((x) => x.prompts.length > 0)
    .map((x) => ({ ...x, missing: missing(x.prompts, values[x.row.templateId] ?? {}) }));
}
/** The value payload for one form: trimmed, empty entries dropped (the same shape the single-form Assign sends). */
export function valuePayload(prompts: HrAssignmentPrompt[], entered: Record<string, string>): Record<string, string> {
  return Object.fromEntries(prompts.map((p) => [p.fieldKey, (entered[p.fieldKey] ?? '').trim()] as const).filter(([, v]) => v));
}

// ─── Running a batch ───────────────────────────────────────────────────────────────────────────────────────────────────────────
export type ItemResult =
  | { state: 'assigned' }
  | { state: 'already'; message: string }
  | { state: 'failed'; message: string };

export interface BatchItem { templateId: string; versionId: string; name: string; fieldValues: Record<string, string> }

/** What the server's refusal means for one item. A 409 that names the existing assignment is a duplicate (shown as Already assigned); everything else is a failure with the server's own words. */
export function resultFromError(e: unknown, permissionText: string): ItemResult {
  const err = e as { status?: number; message?: string; body?: { existingAssignmentId?: string } } | null | undefined;
  if (err?.status === 409 && err.body?.existingAssignmentId) return { state: 'already', message: 'Already assigned to this person.' };
  if (err?.status === 403) return { state: 'failed', message: permissionText };
  const raw = typeof err?.message === 'string' ? err.message.trim() : '';
  return { state: 'failed', message: raw || 'The assignment could not be made.' };
}

/**
 * Assigns the items ONE AT A TIME through the existing writer. `alreadyDone` lists template ids that succeeded earlier in this session: they are never sent again,
 * so a retry cannot duplicate them. A throw from `assign` is that item's failure and never stops the others. `onResult` is called as each item settles.
 */
export async function runAssignBatch(
  items: BatchItem[],
  alreadyDone: ReadonlySet<string>,
  assign: (item: BatchItem) => Promise<void>,
  onResult: (templateId: string, r: ItemResult) => void,
  permissionText: string,
): Promise<Record<string, ItemResult>> {
  const out: Record<string, ItemResult> = {};
  for (const item of items) {
    if (alreadyDone.has(item.templateId)) continue;
    let r: ItemResult;
    try { await assign(item); r = { state: 'assigned' }; } catch (e) { r = resultFromError(e, permissionText); }
    out[item.templateId] = r;
    onResult(item.templateId, r);
  }
  return out;
}

/** The ids to keep selected after a batch: everything that failed (retry), nothing that is now assigned. */
export function selectionAfterBatch(selected: ReadonlySet<string>, results: Record<string, ItemResult>): Set<string> {
  return new Set([...selected].filter((id) => results[id]?.state !== 'assigned' && results[id]?.state !== 'already'));
}

/** Footer acknowledgment ("No documents needed"). Offered only when the server says it is allowed AND the list loaded cleanly; never on a failed read. */
export function noDocumentsAck(allowed: boolean | undefined, listLoaded: boolean, liveCount: number): { show: boolean; reason: string | null } {
  if (liveCount > 0) return { show: false, reason: 'Documents are already assigned, so “No documents needed” can’t be recorded.' };
  if (!listLoaded) return { show: false, reason: 'The documents could not be checked, so “No documents needed” can’t be recorded yet.' };
  if (allowed !== true) return { show: false, reason: null }; // only on the server's explicit "allowed"
  return { show: true, reason: null };
}
